import * as THREE from "three";

class BufferGeometryUtils {
    static triangleIterator(mesh) {
        function* _generator() {
            mesh = BufferGeometryUtils.getLeaf(mesh);
            const geometry = mesh.geometry;
            const positions = geometry.getAttribute("position");
            console.assert(positions.itemSize === 3);
            if (geometry.index) {
                const indices = geometry.index;
                console.assert(indices.count % 3 === 0);
                for (let triangleIdx = 0; triangleIdx < indices.count; triangleIdx += 3) {
                    const triangle = new THREE.Triangle(
                        new THREE.Vector3().fromBufferAttribute(positions, indices.array[triangleIdx]),
                        new THREE.Vector3().fromBufferAttribute(positions, indices.array[triangleIdx + 1]),
                        new THREE.Vector3().fromBufferAttribute(positions, indices.array[triangleIdx + 2]),
                    );
                    mesh.localToWorld(triangle.a);
                    mesh.localToWorld(triangle.b);
                    mesh.localToWorld(triangle.c);
                    yield triangle;
                }
            } else {
                console.assert(positions.count % 3 === 0);
                for (let triangleIdx = 0; triangleIdx < positions.count; triangleIdx += 3) {
                    const triangle = new THREE.Triangle().setFromAttributeAndIndices(
                        positions, triangleIdx, triangleIdx + 1, triangleIdx + 2
                    )
                    mesh.localToWorld(triangle.a);
                    mesh.localToWorld(triangle.b);
                    mesh.localToWorld(triangle.c);
                    yield triangle;
                }
            }
        }

        return _generator();
    }

    static meshCenter(mesh) {
        const geometry = mesh.geometry;
        geometry.computeBoundingBox();
        const center = new THREE.Vector3();
        geometry.boundingBox.getCenter(center);
        // TODO: sometimes this needs to be here and sometimes it needs to not be here. What's going on?
        mesh.localToWorld(center);
        return center;
    }

    static getLeaf(mesh) {
        while (mesh.children && mesh.children.length === 1) {
            mesh = mesh.children[0];
        }
        return mesh;
    }

    static translateVertices(mesh, offset) {
        const positions = mesh.geometry.getAttribute("position");
        console.assert(positions.itemSize === 3);
        for (let i = 0; i < positions.count; i++) {
            positions.setXYZ(
                i,
                positions.getX(i) - offset.x,
                positions.getY(i) - offset.y,
                positions.getZ(i) - offset.z,
            )
        }
        positions.needsUpdate = true;
        mesh.geometry.computeBoundingBox();
        mesh.geometry.computeBoundingSphere();
        // TODO: add, not copy
        mesh.position.copy(offset);
        mesh.updateMatrixWorld();
    }
}

class VectorUtils {
    static averageVectors3(vectors) {
        const average = new THREE.Vector3();
        for (let vector of vectors) {
            average.add(vector);
        }
        average.divideScalar(vectors.length);
        return average;
    }
}

class NumberUtils {
    static weightedAverage(values, weights) {
        console.assert(values.length === weights.length);
        let sum = 0;
        let weightsSum = 0;
        for (let i = 0; i < values.length; i++) {
            sum += values[i] * weights[i];
            weightsSum += weights[i];
        }
        return sum / weightsSum;
    }
}

class CameraUtils {
    static getCameraFrustum(camera) {
        camera.updateMatrix();
        camera.updateMatrixWorld();

        const frustum = new THREE.Frustum();
        frustum.setFromProjectionMatrix(
            new THREE.Matrix4().multiplyMatrices(
                camera.projectionMatrix,
                camera.matrixWorld.clone().invert()
            )
        );
        return frustum;
    }
}

class UiUtils {
    static consumeMouseEvents(...elements) {
        for (let element of elements) {
            element.addEventListener("click", (event) => event.stopPropagation());
            element.addEventListener("dblclick", (event) => event.stopPropagation());
            element.addEventListener("mousemove", (event) => event.stopPropagation());
        }
    }

    static confirmAsync(msg) {
        return new Promise((resolve, reject) => {
            let confirmed = window.confirm(msg);
            return resolve(confirmed);
        });
    }
}

class StringUtils {
    // From https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid.
    static generateId() {
        return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
    }

    static isValidUuid(s) {
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);
    }
}

class FilesUtils {
    static getExtension(filename) {
        const parts = filename.split(".");
        if (parts.length === 1 || (parts[0] === "" && parts.length === 2)) {
            return "";
        }
        return "." + parts.pop();
    }
}

class CookieUtils {
    static setCookie(name, value) {
        document.cookie = name + "=" + value + "; Path=/;";
    }

    static deleteCookie(name) {
        document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    }
}

class DateUtils {
    static isSameDay(date1, date2) {
        return date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    }
}


class AssertUtils {
    static assertDefined(...args) {
        for (let i = 0; i < args.length; i++) {
            console.assert(args[i] !== undefined, `${i + 1}th argument was undefined.`);
        }
    }
}

class SpecialStates {
    static ToBeLoaded = new SpecialStates("to_be_loaded");
    static Loading = new SpecialStates("loading");
    static Empty = new SpecialStates("empty");

    static CreateErrorState(error) {
        return new SpecialStates.ErrorState(error);
    }

    constructor(tag) {
        this.tag = tag;
    }

    toString() {
        return `[${this.tag}]`;
    }

    static ErrorState = class ErrorState {
        constructor(error) {
            this.error = error;
        }
    }
}

// TODO: remove this
class Bus extends THREE.EventDispatcher {
    constructor() {
        super();
        this.listenersMap = new Map();
    }


    addEventListenerWithId(type, listener) {
        super.addEventListener(type, listener);
        const id = StringUtils.generateId();
        this.listenersMap[id] = {type, listener};
        return id;
    }

    removeEventListenerById(id) {
        const {type, listener} = this.listenersMap[id];
        this.listenersMap.delete(id);
        super.removeEventListener(type, listener)
    }

    dispatchEvent(event) {
        console.log("BUS | " + event)
        return super.dispatchEvent(event);
    }
}

function unimplementedFunction(...args) {
    throw new Error("Unimplemented function - was probably left as a default value although it should have been overwritten.");
}

export {
    BufferGeometryUtils,
    VectorUtils,
    NumberUtils,
    CameraUtils,
    UiUtils,
    StringUtils,
    FilesUtils,
    CookieUtils,
    DateUtils,
    AssertUtils,
    Bus,
    SpecialStates,
    unimplementedFunction,
};